/**
 * 프리뷰 위에 얹는 «유튜브 쇼츠 화면» 재현과, 그 UI 가 덮는 자리 표시.
 *
 * 왜 이렇게까지 그리나 — 예전에는 점선 사각형 하나만 그렸는데, 사람들이 그걸
 * **영상이 잘리는 선**으로 읽었다(2026-09-10 실사용자 피드백). 실제로는 잘리는
 * 게 아니라 앱이 그 위를 덮는다. 좋아요·댓글·공유가 그 자리에 실제로 놓여 있으면
 * 설명 없이도 그게 읽힌다.
 *
 * 좌표는 전부 `SHORTS_UI`(1080×1920 기준 px)에서 나온다. 이 파일에 상수를 따로
 * 두지 않는다 — 사본을 들고 있다가 본진과 어긋난 전례가 바로 이 화면이다.
 */
import type { ReactNode } from 'react';
import { CANVAS_HEIGHT, CANVAS_WIDTH, SHORTS_UI } from '@repo/editor-core';

/** 앱 UI 가 덮는 두께, 캔버스 대비 비율. */
const TOP = SHORTS_UI.top / CANVAS_HEIGHT;
const BOTTOM = SHORTS_UI.bottom / CANVAS_HEIGHT;
const SIDE = SHORTS_UI.side / CANVAS_WIDTH;

const pct = (n: number) => `${(n * 100).toFixed(4)}%`;

/* ── 아이콘 (lucide) ────────────────────────────────────────────────
   에디터에는 아이콘 패키지가 없다. 이 화면에 쓰는 것만 인라인한다. */
const GLYPHS = {
  back: <path d="m15 18-6-6 6-6" />,
  search: (
    <>
      <path d="m21 21-4.34-4.34" />
      <circle cx="11" cy="11" r="8" />
    </>
  ),
  camera: (
    <>
      <path d="M13.997 4a2 2 0 0 1 1.76 1.05l.486.9A2 2 0 0 0 18.003 7H20a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1.997a2 2 0 0 0 1.759-1.048l.489-.904A2 2 0 0 1 10.004 4z" />
      <circle cx="12" cy="13" r="3" />
    </>
  ),
  more: (
    <>
      <circle cx="12" cy="12" r="1" />
      <circle cx="19" cy="12" r="1" />
      <circle cx="5" cy="12" r="1" />
    </>
  ),
  like: (
    <>
      <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" />
      <path d="M7 10v12" />
    </>
  ),
  dislike: (
    <>
      <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z" />
      <path d="M17 14V2" />
    </>
  ),
  comment: (
    <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719" />
  ),
  share: (
    <>
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.59" x2="15.42" y1="13.51" y2="17.49" />
      <line x1="15.41" x2="8.59" y1="6.51" y2="10.49" />
    </>
  ),
  waveform: (
    <>
      <path d="M2 10v3" />
      <path d="M6 6v11" />
      <path d="M10 3v18" />
      <path d="M14 8v7" />
      <path d="M18 5v13" />
      <path d="M22 10v3" />
    </>
  ),
  music: (
    <>
      <path d="M9 18V5l12-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
    </>
  ),
  home: (
    <>
      <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" />
      <path d="M3 10a2 2 0 0 1 .709-1.528l7-6a2 2 0 0 1 2.582 0l7 6A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </>
  ),
  shorts: (
    <>
      <path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5" />
      <rect x="2" y="6" width="14" height="12" rx="2" />
    </>
  ),
  plus: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M8 12h8" />
      <path d="M12 8v8" />
    </>
  ),
  subs: (
    <>
      <rect width="18" height="18" x="3" y="3" rx="2" />
      <path d="M21 9H3" />
      <path d="M21 15H3" />
    </>
  ),
  me: (
    <>
      <circle cx="12" cy="8" r="5" />
      <path d="M20 21a8 8 0 0 0-16 0" />
    </>
  ),
} satisfies Record<string, ReactNode>;

function Icon({ g, px, sw = 1.9 }: { g: keyof typeof GLYPHS; px: number; sw?: number }) {
  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,.6))', flex: '0 0 auto' }}
    >
      {GLYPHS[g]}
    </svg>
  );
}

/**
 * 덮이는 자리를 어둡게 깔고 안전 영역의 네 모서리를 표시한다.
 *
 * 🔴 **SVG 한 장으로 그린다.** div + `border` 로 모서리 표시를 그렸더니 Chrome 이
 * 그 자리를 통째로 다시 칠하면서 아래에 있던 로고·제목을 지워 버렸다(실측 2026-09-10:
 * 계산된 좌표는 멀쩡한데 화면만 어긋났다). 레이어가 하나면 그 일이 생기지 않고,
 * 좌표를 1080×1920 그대로 쓸 수 있어 `layout.ts` 와 눈으로 대조된다.
 */
export function DeadZoneGuide() {
  const { top, side, bottom } = SHORTS_UI;
  const innerW = CANVAS_WIDTH - side * 2;
  const innerH = CANVAS_HEIGHT - top - bottom;
  const right = CANVAS_WIDTH - side;
  const floor = CANVAS_HEIGHT - bottom;
  const mark = 82; // 모서리 표시 길이(px @1080×1920)

  return (
    <svg
      viewBox={`0 0 ${CANVAS_WIDTH} ${CANVAS_HEIGHT}`}
      preserveAspectRatio="none"
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full"
      style={{ zIndex: 4 }}
    >
      {/* 바깥은 어둡게, 안전 영역만 도려낸다 (evenodd) */}
      <path
        fill="rgba(0,0,0,.46)"
        fillRule="evenodd"
        d={`M0 0h${CANVAS_WIDTH}v${CANVAS_HEIGHT}H0z M${side} ${top}h${innerW}v${innerH}H${side}z`}
      />
      <g
        fill="none"
        stroke="#fff"
        strokeOpacity=".72"
        strokeWidth="2"
        vectorEffect="non-scaling-stroke"
      >
        <path d={`M${side} ${top + mark}V${top}h${mark}`} />
        <path d={`M${right} ${top + mark}V${top}h-${mark}`} />
        <path d={`M${side} ${floor - mark}v${mark}h${mark}`} />
        <path d={`M${right} ${floor - mark}v${mark}h-${mark}`} />
      </g>
    </svg>
  );
}

/**
 * 유튜브 쇼츠 앱 화면 재현 — 상단 검색·뒤로가기, 우측 액션 버튼, 하단 채널·음악·탭바.
 *
 * 숫자는 «이 자리에 이런 게 온다»를 보여주는 예시다. 실제 지표가 아니다.
 * @param w 캔버스(영상) 가로 픽셀. 모든 크기가 여기서 나온다 — 프리뷰 폭이 바뀌면
 *          글자와 아이콘도 같은 비율로 따라가야 «실제 앱에서 이만큼»이 유지된다.
 * @param channelName 하단에 앱이 띄우는 채널 이름.
 */
export function ShortsChrome({ w, channelName }: { w: number; channelName: string }) {
  const u = (n: number) => w * n; // 캔버스 폭 대비 크기
  const tabH = u(0.105);

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 text-white"
      style={{ zIndex: 5 }}
    >
      {/* 상단 — 뒤로가기 / 검색 · 카메라 · 더보기 */}
      <div
        className="absolute inset-x-0 top-0 flex items-end justify-between"
        style={{ height: pct(TOP), padding: `0 ${u(0.04)}px ${u(0.013)}px` }}
      >
        <Icon g="back" px={u(0.055)} sw={2.2} />
        <span className="flex" style={{ gap: u(0.05) }}>
          <Icon g="search" px={u(0.055)} sw={2.2} />
          <Icon g="camera" px={u(0.055)} sw={2.2} />
          <Icon g="more" px={u(0.055)} sw={2.4} />
        </span>
      </div>

      {/* 우측 액션 버튼 */}
      <div
        className="absolute right-0 flex flex-col items-center"
        style={{
          bottom: pct(BOTTOM),
          width: pct(SIDE),
          gap: u(0.042),
          paddingBottom: u(0.05),
        }}
      >
        {(
          [
            ['like', '3.7만'],
            ['dislike', '싫어요'],
            ['comment', '1.1천'],
            ['share', '공유'],
          ] as const
        ).map(([g, label]) => (
          <span key={g} className="flex flex-col items-center" style={{ gap: u(0.008) }}>
            <Icon g={g} px={u(0.072)} />
            <span
              className="font-semibold"
              style={{ fontSize: u(0.032), textShadow: '0 1px 2px rgba(0,0,0,.7)' }}
            >
              {label}
            </span>
          </span>
        ))}
        <span
          className="grid place-items-center"
          style={{
            width: u(0.085),
            height: u(0.085),
            borderRadius: u(0.018),
            background: '#3a2a1e',
            border: '1px solid rgba(255,255,255,.35)',
          }}
        >
          <Icon g="waveform" px={u(0.051)} sw={2} />
        </span>
      </div>

      {/* 하단 — 채널 · 설명 · 음악, 그리고 앱 탭바 */}
      <div
        className="absolute inset-x-0 bottom-0 flex flex-col justify-end"
        style={{
          height: pct(BOTTOM),
          padding: `0 ${u(0.038)}px ${tabH + u(0.026)}px`,
          gap: u(0.015),
        }}
      >
        <div className="flex items-center" style={{ gap: u(0.02) }}>
          <span
            className="shrink-0 rounded-full bg-neutral-500 dark:bg-neutral-400"
            style={{ width: u(0.064), height: u(0.064) }}
          />
          <span className="truncate font-semibold" style={{ fontSize: u(0.036) }}>
            @{channelName}
          </span>
          <span
            className="shrink-0 rounded-full font-bold"
            style={{
              fontSize: u(0.032),
              background: '#ff0033',
              padding: `${u(0.009)}px ${u(0.028)}px`,
            }}
          >
            구독
          </span>
        </div>
        <div className="truncate" style={{ fontSize: u(0.034), opacity: 0.92 }}>
          하이라이트 #쇼츠
        </div>
        <div className="flex items-center" style={{ gap: u(0.018), opacity: 0.88 }}>
          <Icon g="music" px={u(0.042)} sw={2} />
          <span className="truncate" style={{ fontSize: u(0.031) }}>
            원본 오디오 · {channelName}
          </span>
        </div>
      </div>

      <div
        className="absolute inset-x-0 bottom-0 flex items-center justify-around"
        style={{ height: tabH, background: 'rgba(0,0,0,.55)', fontSize: u(0.028) }}
      >
        {(
          [
            ['home', '홈', false],
            ['shorts', 'Shorts', true],
            ['plus', '', false],
            ['subs', '구독', false],
            ['me', '나', false],
          ] as const
        ).map(([g, label, on]) => (
          <span
            key={g}
            className="flex flex-col items-center"
            style={{ gap: u(0.006), opacity: on ? 1 : 0.75 }}
          >
            <Icon g={g} px={u(0.05)} />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}
